<template>
  <b-card class="bulk-patient-import-form">
    <h5 class="mb-4">
      <i class="fas fa-file-import mr-2 text-primary"></i>
      Bulk Patient Import
    </h5>
    <b-form @submit.prevent="handleSubmit">
      <ClinicSearchSelect
        v-model="selectedClinic"
        label="Select Clinic *"
        :state="$v.selectedClinic.$dirty ? !$v.selectedClinic.$error : null"
      />
      <b-form-invalid-feedback :state="!$v.selectedClinic.$error">
        Please select a clinic
      </b-form-invalid-feedback>

      <b-form-group label="Select Branch *" class="mt-3">
        <b-form-select
          v-model="selectedBranchId"
          :options="branchOptions"
          :disabled="!selectedClinic"
          :state="$v.selectedBranchId.$dirty ? !$v.selectedBranchId.$error : null"
          @change="$v.selectedBranchId.$touch()"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>
              {{ selectedClinic ? 'Choose a branch...' : 'Select a clinic first' }}
            </b-form-select-option>
          </template>
        </b-form-select>
        <b-form-invalid-feedback :state="!$v.selectedBranchId.$error">
          Please select a branch
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="File Format *" class="mt-3">
        <b-form-radio-group v-model="format" stacked>
          <b-form-radio value="dentcloud">
            DentCloud Excel
            <small class="text-muted d-block">
              .xls/.xlsx with camelCase headers in row 1
            </small>
          </b-form-radio>
          <b-form-radio value="legacy">
            ไฟล์จากโปรแกรมเดิม (Patient Report)
            <small class="text-muted d-block">
              The export exactly as the old dental software wrote it. The legacy DN
              is kept in existDn and a new DentCloud DN derived from it.
            </small>
          </b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <ExcelFileUploader
        v-model="file"
        :label="format === 'legacy' ? 'Upload Patient Report *' : 'Upload Excel File *'"
        :state="$v.file.$dirty ? !$v.file.$error : null"
        class="mt-3"
        @input="$v.file.$touch()"
      />
      <b-form-invalid-feedback :state="!$v.file.$error">
        File is required
      </b-form-invalid-feedback>

      <b-form-checkbox v-if="format === 'legacy'" v-model="dryRun" class="mt-3">
        ตรวจสอบก่อน (dry run) — report what would happen without writing
      </b-form-checkbox>

      <b-button type="submit" variant="primary" :disabled="loading" block class="mt-3">
        <i class="fas mr-2" :class="loading ? 'fa-spinner fa-spin' : 'fa-file-import'"></i>
        {{ submitLabel }}
      </b-button>
    </b-form>
  </b-card>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapGetters, mapActions } from 'vuex';
import ExcelFileUploader from './ExcelFileUploader.vue';
import ClinicSearchSelect from './ClinicSearchSelect.vue';

export default {
  name: 'BulkPatientImportForm',
  components: { ExcelFileUploader, ClinicSearchSelect },
  data() {
    return {
      selectedClinic: null,
      selectedBranchId: null,
      file: null,
      format: 'dentcloud',
      dryRun: true
    };
  },
  validations: {
    selectedClinic: { required },
    selectedBranchId: { required },
    file: { required }
  },
  computed: {
    ...mapGetters('moduleSuperadmin', ['isImportPatientsLoading']),
    loading() {
      return this.isImportPatientsLoading;
    },
    submitLabel() {
      if (this.loading) return this.isDryRun ? 'Checking...' : 'Importing Patients...';
      return this.isDryRun ? 'ตรวจสอบไฟล์ (Dry Run)' : 'Import Patients';
    },
    isDryRun() {
      return this.format === 'legacy' && this.dryRun;
    },
    branchOptions() {
      if (!this.selectedClinic || !this.selectedClinic.branches) return [];
      return this.selectedClinic.branches.map(branch => ({
        value: branch.branch_id || branch.branchId,
        text: `${branch.branch_code || branch.branchCode} - ${branch.branch_name_th || branch.branchNameTh}`
      }));
    }
  },
  watch: {
    selectedClinic() {
      this.selectedBranchId = null;
    }
  },
  methods: {
    ...mapActions('moduleSuperadmin', ['importPatients']),
    async handleSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('clinicId', String(this.selectedClinic.clinicId || this.selectedClinic.id));
      formData.append('branchId', String(this.selectedBranchId));
      if (this.format === 'legacy') {
        formData.append('format', 'legacy');
        if (this.dryRun) formData.append('dryRun', 'true');
      }
      try {
        const result = await this.importPatients(formData);
        this.$emit('success', result.data);
        // A dry run leaves the form loaded so the same file can be imported
        // for real once the report has been read.
        if (!this.isDryRun) this.resetForm();
      } catch (error) {
        const message = error.response?.data?.error || 'Failed to import patients';
        this.$bvToast.toast(message, {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 5000
        });
      }
    },
    resetForm() {
      this.selectedClinic = null;
      this.selectedBranchId = null;
      this.file = null;
      this.format = 'dentcloud';
      this.dryRun = true;
      this.$v.$reset();
    }
  }
};
</script>
